import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { Provider, useDispatch, useSelector } from 'react-redux';
import './App.css';
import brandingConfig from './config/brandingConfig';

// Store
import { store } from './redux/store';
import { checkAuthState, hasPortalAccess } from './redux/slices/authSlice';

// Theme
import theme from './styles/theme';

// Components
import Navbar from './components/common/Navbar';
import LandingPage from './components/auth/LandingPage';
import Dashboard from './components/dashboard/Dashboard';
import Loading from './components/common/Loading';
import CookieBanner from './components/common/CookieBanner';
import PrivacyPolicy from './components/auth/PrivacyPolicy';
import Imprint from './components/auth/Imprint';

// Providers
import AppProvider from './providers/AppProvider';

// Fahrer-Komponenten
import DriversList from './components/drivers/DriversList';
import DriverDetails from './components/drivers/DriverDetails';
import DriverForm from './components/drivers/DriverForm';

// Kunden-Komponenten
import CustomersList from './components/customers/CustomersList';
import CustomerDetails from './components/customers/CustomerDetails';
import CustomerForm from './components/customers/CustomerForm';
import InvoiceCustomersPage from './components/customers/InvoiceCustomersPage';

// Fahrzeug-Komponenten
import VehiclesList from './components/vehicles/VehiclesList';
import VehicleDetails from './components/vehicles/VehicleDetails';
import VehicleForm from './components/vehicles/VehicleForm';

// Fahrt-Komponenten
import RidesList from './components/rides/RidesList';
import RideDetails from './components/rides/RideDetails';
import RecurringOrdersList from './components/rides/RecurringOrdersList';
import RecurringOrderEditForm from './components/rides/RecurringOrderEditForm';
import MedicalTransportOrdersList from './components/medicalTransportOrders/MedicalTransportOrdersList';
import MedicalTransportOrderForm from './components/medicalTransportOrders/MedicalTransportOrderForm';
import MedicalTransportOrderDetails from './components/medicalTransportOrders/MedicalTransportOrderDetails';
import MedicalTransportBillingPage from './components/medicalTransportBilling/MedicalTransportBillingPage';

// Schicht-Komponenten
import ShiftsList from './components/shifts/ShiftsList';
import ShiftDetails from './components/shifts/ShiftDetails';
import ShiftForm from './components/shifts/ShiftForm';
import ShiftCalendar from './components/shifts/ShiftCalendar';
import ShiftPlanner from './components/shifts/ShiftPlanner';
import PlannedShiftForm from './components/shifts/PlannedShiftForm';

// Berichts-Komponente
import ReportsPage from './components/reports/ReportsPage';

// Einstellungen-Komponente
import SettingsPage from './components/settings/SettingsPage';
import TelephonyConnectorGuidePage from './components/settings/TelephonyConnectorGuidePage';
import CompanyEventInfoPage from './components/customers/CompanyEventInfoPage';
import PayPalSetupGuidePage from './components/settings/PayPalSetupGuidePage';
import VenueChannelSetupGuidePage from './components/settings/VenueChannelSetupGuidePage';
import SectorManagementPage from './components/sectors/SectorManagementPage';
import ProfilePage from './components/profile/ProfilePage';
import OperatorsPage from './components/operators/OperatorsPage';

// Öffentliche Seiten
import MobileAppRedirect from './components/public/MobileAppRedirect';
import MobilePrivacyPolicy from './components/mobile/MobilePrivacyPolicy';
import MobileImprint from './components/mobile/MobileImprint';
import AccountDeletion from './components/mobile/AccountDeletion';
import IncomingCallNotifier from './components/telephony/IncomingCallNotifier';
import CriticalNotificationNotifier from './components/notifications/CriticalNotificationNotifier';
import { RideDialogProvider, useRideDialog } from './components/rides/RideDialogProvider';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useSelector(state => state.auth);
  
  if (isLoading) {
    return <Loading message="Authentifizierung wird überprüft..." />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!hasPortalAccess(user)) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Öffnet für alte Deep-Links/Bookmarks (/fahrten/neu, /fahrten/:id/bearbeiten,
// /fahrten/:id/zuweisen) den globalen Dialog und leitet auf eine Basisseite um,
// da RideForm keine eigene Seite mehr ist.
const RideDialogRedirect = ({ mode }) => {
  const { id } = useParams();
  const { openRideDialog } = useRideDialog() || {};
  const navigate = useNavigate();
  useEffect(() => {
    openRideDialog?.({ mode, rideId: id });
    navigate(mode === 'create' ? '/fahrten' : `/fahrten/${id}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
};

// Auth Check Wrapper
const AuthCheckWrapper = ({ children }) => {
  const dispatch = useDispatch();
  const { isLoading } = useSelector(state => state.auth);
  
  useEffect(() => {
    dispatch(checkAuthState());
  }, [dispatch]);
  
  if (isLoading) {
    return <Loading message="Anwendung wird geladen..." />;
  }
  
  return children;
};

function AppContent() {
  const { isAuthenticated } = useSelector(state => state.auth);
  
  // Setze Dokumententitel und Favicon basierend auf Branding-Konfiguration
  useEffect(() => {
    // Setze Dokumententitel
    document.title = `${brandingConfig.appName} - Admin Portal`;
    
    // Setze Theme-Color Meta-Tag auf die Primärfarbe
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', brandingConfig.colors.primary);
    }
  }, []);
  
  // Prüfe, ob es sich um die Default-App handelt (NextCab) oder um eine White-Label-App
  const isDefaultApp = brandingConfig.appName === "NextCab";

  return (
    <Router>
      <AuthCheckWrapper>
       <RideDialogProvider>
        {isAuthenticated && <Navbar />}
        {isAuthenticated && <IncomingCallNotifier />}
        {isAuthenticated && <CriticalNotificationNotifier />}
        <Routes>
          {/* Public Routes */}
          <Route
            path="/login"
            element={
              isAuthenticated
                ? <Navigate to="/dashboard" />
                : <LandingPage directLogin={!isDefaultApp} />
            }
          />

          {/* Rechtliche Seiten - öffentlich zugänglich */}
          <Route 
            path="/datenschutz" 
            element={<PrivacyPolicy />} 
          />
          <Route 
            path="/impressum" 
            element={<Imprint />} 
          />
          <Route
            path="/aktion/:code"
            element={<CompanyEventInfoPage />}
          />
          <Route
            path="/mobile-app"
            element={<MobileAppRedirect />}
          />
          <Route
            path="/mobile/datenschutz"
            element={<MobilePrivacyPolicy />}
          />
          <Route
            path="/mobile/impressum"
            element={<MobileImprint />}
          />
          <Route
            path="/mobile/konto-loeschen"
            element={<AccountDeletion />}
          />
        
          {/* Protected Routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/fahrer" 
            element={
              <ProtectedRoute>
                <DriversList />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/fahrer/:id" 
            element={
              <ProtectedRoute>
                <DriverDetails />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/fahrer/:id/bearbeiten" 
            element={
              <ProtectedRoute>
                <DriverForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/fahrer/neu" 
            element={
              <ProtectedRoute>
                <DriverForm />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/kunden"
            element={
              <ProtectedRoute>
                <CustomersList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rechnungskunden"
            element={
              <ProtectedRoute>
                <InvoiceCustomersPage />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/kunden/:id" 
            element={
              <ProtectedRoute>
                <CustomerDetails />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/kunden/:id/bearbeiten" 
            element={
              <ProtectedRoute>
                <CustomerForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/kunden/neu" 
            element={
              <ProtectedRoute>
                <CustomerForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/fahrzeuge" 
            element={
              <ProtectedRoute>
                <VehiclesList />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/fahrzeuge/:id" 
            element={
              <ProtectedRoute>
                <VehicleDetails />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/fahrzeuge/:id/bearbeiten" 
            element={
              <ProtectedRoute>
                <VehicleForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/fahrzeuge/neu" 
            element={
              <ProtectedRoute>
                <VehicleForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/fahrten" 
            element={
              <ProtectedRoute>
                <RidesList />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/fahrten/:id" 
            element={
              <ProtectedRoute>
                <RideDetails />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/fahrten/:id/bearbeiten"
            element={
              <ProtectedRoute>
                <RideDialogRedirect mode="edit" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/fahrten/:id/zuweisen"
            element={
              <ProtectedRoute>
                <RideDialogRedirect mode="assign" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/fahrten/neu"
            element={
              <ProtectedRoute>
                <RideDialogRedirect mode="create" />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/fahrten/dauerauftraege" 
            element={
              <ProtectedRoute>
                <RecurringOrdersList />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/fahrten/dauerauftraege/:recurringOrderId/bearbeiten" 
            element={
              <ProtectedRoute>
                <RecurringOrderEditForm />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/krankenfahrten"
            element={
              <ProtectedRoute>
                <MedicalTransportOrdersList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/krankenfahrten/neu"
            element={
              <ProtectedRoute>
                <MedicalTransportOrderForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/krankenfahrten/abrechnung"
            element={
              <ProtectedRoute>
                <MedicalTransportBillingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/krankenfahrten/:id/erneuern"
            element={
              <ProtectedRoute>
                <MedicalTransportOrderForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/krankenfahrten/:id"
            element={
              <ProtectedRoute>
                <MedicalTransportOrderDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/krankenfahrten/:id/bearbeiten"
            element={
              <ProtectedRoute>
                <MedicalTransportOrderForm />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/schichten" 
            element={
              <ProtectedRoute>
                <ShiftsList />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/schichten/kalender" 
            element={
              <ProtectedRoute>
                <ShiftCalendar />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/schichten/:id" 
            element={
              <ProtectedRoute>
                <ShiftDetails />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/schichten/:id/bearbeiten" 
            element={
              <ProtectedRoute>
                <ShiftForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/schichten/neu" 
            element={
              <ProtectedRoute>
                <ShiftForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/schichtplaner" 
            element={
              <ProtectedRoute>
                <ShiftPlanner />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/schichtplaner/neu" 
            element={
              <ProtectedRoute>
                <PlannedShiftForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/schichtplaner/:id/bearbeiten" 
            element={
              <ProtectedRoute>
                <PlannedShiftForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/berichte" 
            element={
              <ProtectedRoute>
                <ReportsPage />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/einstellungen"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/einstellungen/sektoren"
            element={
              <ProtectedRoute>
                <SectorManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/einstellungen/telefonie-anleitung"
            element={
              <ProtectedRoute>
                <TelephonyConnectorGuidePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/einstellungen/paypal-einrichtung"
            element={
              <ProtectedRoute>
                <PayPalSetupGuidePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/einstellungen/bestellknopf-einrichtung"
            element={
              <ProtectedRoute>
                <VenueChannelSetupGuidePage />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/profil" 
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/profil/unternehmer" 
            element={
              <ProtectedRoute>
                <OperatorsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/profil/sub-konten" 
            element={
              <ProtectedRoute>
                <OperatorsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/passwort-vergessen" 
            element={
              <div>Passwort vergessen (noch nicht implementiert)</div>
            } 
          />
          
          {/* Default Route */}
          <Route
            path="/"
            element={
              isAuthenticated
                ? <Navigate to="/dashboard" />
                : <LandingPage directLogin={!isDefaultApp} />
            }
          />
          
          {/* 404 Route */}
          <Route 
            path="*" 
            element={
              <div>Seite nicht gefunden</div>
            } 
          />
        </Routes>
        
        {/* Cookie-Banner für alle Benutzer anzeigen */}
        <CookieBanner />
       </RideDialogProvider>
      </AuthCheckWrapper>
    </Router>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppProvider>
          <AppContent />
        </AppProvider>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
